<script setup lang="ts">
import { computed, defineAsyncComponent, ref, watch, onMounted } from 'vue'
import { IonContent, IonPage, IonRefresher, IonRefresherContent } from '@ionic/vue'
import type { RefresherCustomEvent } from '@ionic/vue'
import { useRouter } from 'vue-router'
import { openScreen } from '../nav-origin.js'
import LineLedger from '../components/LineLedger.vue'
import { couldntReach } from '../source-copy.js'
import type { LedgerLine } from '../components/LineLedger.vue'
import SettingsCog from '../components/SettingsCog.vue'
// What a shared link could not restore, if anything. See src/share-open.js.
import { shareNote, clearShareNote } from '../share-open.js'
import {
  lineList, ledger, quietWindows, refresh, refreshing, lastRefreshedAt,
  refreshError, refreshBusy, statusFor, clock, cityInfo, city, setModeOn, shownModes, offeredModes,
} from '../app-state.js'
import { networkFailed } from '../network-data.js'
import {
  markModeIntroSeen, modeIntroSeen, modeOn, offersModeIntro,
} from '../mode-state.js'
import { modeOfRow } from '../modes.js'
import { useModeFilter } from '../use-mode-filter.js'
// The chips are a mode thing: out of the first load, like every mode surface.
const ModeFilter = defineAsyncComponent(() => import('../components/ModeFilter.vue'))

// Fetched only when it is offered: the card is said once, and a phone that
// has answered it should not download it on every launch.
const ModeIntro = defineAsyncComponent(() => import('../components/ModeIntro.vue'))

const router = useRouter()

/** The watched lines, worst first. Comes from the cached disruption poll. */
const lines = computed<LedgerLine[]>(() => ledger.value as LedgerLine[])

// Trouble first, inside each section. Same order the ledger itself uses — the
// bad row is what the app is opened for, and that is as true of the line
// somebody watches as of the one they are about to ask about.
const RANK: Record<string, number> = { suspended: 0, delayed: 1, planned: 2, good: 3 }

const watched = computed(() => new Set(lines.value.map((l) => Number(l.routeId))))

/**
 * The rest of the network, read live and rendered quieter.
 *
 * Built here rather than in app-state because it is a SCREEN's answer, not the
 * ledger's: `ledger` is the watched subscriptions and stays that, and these
 * rows have no subscription id to carry. The key is the route id with a prefix
 * so it cannot collide with a subscription id in the section above.
 *
 * `lineList` is the CITY'S lines — Melbourne's seventeen in the network map's
 * grouping, Sydney's off the bundle — so "the rest of the network" means the
 * rest of the network somebody is actually looking at.
 */
const others = computed<LedgerLine[]>(() => lineList.value
  .filter((line) => !watched.value.has(Number(line.routeId)))
  // A MODE'S ROUTES ARE HERE TOO (Melbourne's trams, for a phone that has them
  // on), in a group of their own that is collapsed unless one is in trouble:
  // twenty-four rows would double the screen if they were just listed, and one
  // line away is the question somebody asks about a route they do not watch.
  .map((line) => {
    const state = statusFor(line.routeId)
    return {
      id: `route-${line.routeId}`,
      routeId: line.routeId,
      name: line.name,
      colour: line.colour,
      // Carried so the sort below can group without re-reading `lineList`.
      regional: Boolean(line.regional),
      // The group a row sits in, and what a numbered row prints.
      mode: line.mode,
      short: line.short,
      ends: line.ends,
      status: state.status,
      delayMinutes: state.delayMinutes,
      note: state.note,
      replacement: state.replacement ?? null,
      disruptionId: state.top ? String(state.top.id) : null,
    } as LedgerLine
  })
  // TROUBLE FIRST, then metropolitan before regional, then by name.
  //
  // The middle term arrived with V/Line. Sorted on name alone, thirteen purple
  // rows interleave with the metropolitan ones — Albury, Alamein, Ararat,
  // Bairnsdale, Ballarat, Belgrave — and the palette that tells a Melbourne
  // commuter which group a line belongs to becomes noise in a list of thirty.
  // Grouped, the metropolitan network reads as it always did and the regional
  // lines read as a block, without a heading that would split one section into
  // two on the screen somebody opens to answer one question.
  //
  // IT IS THE SECOND TERM, NOT THE FIRST, and that is the point: a suspended
  // Geelong line still sorts above a metropolitan line running to timetable.
  // The grouping is for the twenty-eight rows that have nothing to say.
  .sort((a, b) => RANK[a.status] - RANK[b.status]
    || Number(Boolean(a.regional)) - Number(Boolean(b.regional))
    || a.name.localeCompare(b.name, undefined, { numeric: true })))

// The stamp is the last time the FEED was read, not the time now. A clock that
// keeps ticking while the data behind it is forty minutes stale is the one
// thing a status board must never do.
const now = ref<Date>(lastRefreshedAt.value ?? new Date())

const stamp = computed(() => {
  const day = now.value
    .toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
    .toUpperCase()
  // Nothing has ever been read: not on this install, not from storage. Every
  // row below says "running to timetable" because nobody has said otherwise,
  // and printing the current time over that would turn an absence of news into
  // a bulletin. It is the same failure as the app reporting "Alerts are on"
  // while being unable to show one.
  if (!lastRefreshedAt.value) return `${day} · NOT READ`
  const time = now.value
    .toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })
    .toUpperCase()
  return `${day} · ${time}`
})

/**
 * How old the ledger is allowed to get before it says so.
 *
 * The app polls every ninety seconds while it is in front of someone, so ten
 * minutes means something has actually gone wrong — no signal on a train, a
 * phone that slept, PTV down. Below that the stamp in the masthead is the whole
 * story and a second line about it would be noise on a screen whose whole
 * argument is that nothing decorative competes with the data.
 *
 * Nothing is cleared and nothing is invented: the rows stay, with their age.
 */
const STALE_MS = 10 * 60_000

// Re-read after every poll, including the ones that fail — a failed poll is
// exactly when this line has to appear, and `lastRefreshedAt` does not move on
// one, so it cannot be what triggers the recheck.
const checkedAt = ref(Date.now())
watch(refreshing, (busy) => { if (!busy) checkedAt.value = Date.now() })

const asOf = computed(() => {
  const at = lastRefreshedAt.value
  // A BUDGET REFUSAL IS NOT AN OUTAGE, even in a four word stamp. This app
  // rations its own calls; saying the feed could not be reached because we
  // spent our own minute is the alarming version of a fact that is not
  // alarming at all.
  if (!at) {
    if (refreshBusy.value) return 'BUSY FOR A MOMENT'
    return refreshError.value ? couldntReach().toUpperCase() : null
  }
  return checkedAt.value - at.getTime() > STALE_MS ? `AS OF ${clock(at)}` : null
})

// The first window, and a count of the rest. Settings holds several now, and
// a chip that names one while three are in force is a chip that gets blamed
// for the alert that never arrived.
const quietHours = computed(() => {
  const [first, ...rest] = quietWindows.value
  if (!first?.from || !first?.to) return null
  return { from: first.from, until: first.to, more: rest.length }
})

async function reload() {
  await refresh()
  now.value = lastRefreshedAt.value ?? new Date()
}

onMounted(() => {
  if (lastRefreshedAt.value) now.value = lastRefreshedAt.value
})

async function doRefresh(event: RefresherCustomEvent) {
  await reload()
  event.target.complete()
}

function openDisruption(line: LedgerLine) {
  // A ROUTE OF A MODE OPENS ITS ROUTE, disrupted or not: the route screen leads
  // with the notice and then draws which stops it touches, which is the
  // question a diversion raises and the train disruption screen cannot draw.
  const mode = modeOfRow({ routeId: line.routeId, mode: line.mode })
  if (mode && line.routeId != null) {
    openScreen(router, `/tabs/${mode.id}/${line.routeId}`)
    return
  }
  // A disrupted line opens its disruption. A line running to timetable used
  // to open nothing, which read as a dead tap; it opens the line itself —
  // the strip with your station, and the station notices that never reach
  // the ledger.
  if (line.disruptionId) openScreen(router, `/tabs/disruption/${line.disruptionId}`)
  else if (line.routeId != null) openScreen(router, `/tabs/line/${line.routeId}`)
}

/**
 * The drawn network, from the tab the lines live on.
 *
 * IN THE MASTHEAD, above the ledger. It is a screen about the network rather
 * than about this morning, and the ledger is where somebody is standing when
 * they stop asking "is it running" and start asking "where does it actually
 * go" — which is a question they have at the top of the screen, before they
 * have read a row, and not one they should have to scroll past seventeen lines
 * to ask. It was in the footer, where it was found rather than offered.
 */
function openMap() {
  // Pushed, with a way back: Signal Box took the map's tab slot.
  openScreen(router, '/tabs/map')
}

// THE SHARED MODE FILTER (src/use-mode-filter.js), the board's own setting:
// a chip for each service that is on in Settings, and it shows or hides that
// service's lines in the watched section and in Other lines alike.
const filter = useModeFilter({ only: () => ['train', ...shownModes.value.map((m: any) => m.id)] })
const chipOf = (line: LedgerLine) => {
  const id = line.regional ? 'train' : (line.mode || 'train')
  return filter.chips.value.some((c: any) => c.id === id) ? id : 'train'
}
const chipShows = (line: LedgerLine) => !filter.chips.value.length || filter.isOn(chipOf(line))
const shownLines = computed(() => lines.value.filter(chipShows))
const shownOthers = computed(() => others.value.filter(chipShows))

function editLines() {
  openScreen(router, '/tabs/lines')
}

/**
 * A network that never says anything is disrupted.
 *
 * Transport Canberra publishes no service alerts at all — no endpoint, and zero
 * alert entities in the one file it does publish — so every row of this ledger
 * reads "Running to timetable" for ever, and will whatever happens on the
 * track. That is a true statement about the feed and a MISLEADING one about the
 * railway, and the gap between them is exactly what this app is for.
 *
 * So the ledger says so, under itself, once. Not an error and not a warning:
 * a line of prose in the same voice as the rest, so that somebody who sees
 * green all week knows whether that means "nothing is wrong" or "nobody is
 * saying". Every other city has `hasAlerts: true` and this renders nothing.
 */
const noAlerts = computed(() => cityInfo.value.hasAlerts === false)

/**
 * And a network that publishes nothing live either. Perth's only open data is
 * its timetable, so the sentence above would be wrong in its second half:
 * nothing there is live. The same place, the other sentence.
 */
const notLive = computed(() => cityInfo.value.live === false)

/**
 * The one-time "You can add trams now", for somebody who already watches a
 * train line in a city that declares the mode and has never answered it. See
 * `offersModeIntro`. One card at a time, the first mode still unanswered.
 */
const introMode = computed(() => offeredModes.value.find((mode) => offersModeIntro({
  mode, city: city.value, on: modeOn(mode), seen: modeIntroSeen(mode), watchedCount: lines.value.length,
})) ?? null)

/** Yes: the mode on, and its chip lit; its lines are in Other lines. Choose lines is still the way to watch one. */
function addMode() {
  const mode = introMode.value
  if (!mode) return
  markModeIntroSeen(mode)
  setModeOn(mode, true)
  filter.show(mode.id)
}

function dismissIntro() {
  if (introMode.value) markModeIntroSeen(introMode.value)
}
</script>

<template>
  <ion-page>
    <ion-content :fullscreen="true" class="page">
      <ion-refresher slot="fixed" @ionRefresh="doRefresh">
        <ion-refresher-content pulling-text="" refreshing-spinner="dots" />
      </ion-refresher>

      <header class="la-masthead">
        <settings-cog />
        <p class="la-stamp">{{ stamp }}</p>
        <h1 class="la-title">Your lines</h1>

        <!-- A LINK THAT COULD NOT LAND WHERE IT WAS AIMED says so here: this
             is where share-open.js sends a line this city does not run.
             Same sentence, same place, same dismissal as the board's. -->
        <button
          v-if="shareNote"
          type="button"
          class="share-note"
          @click="clearShareNote"
        >{{ shareNote }}</button>
      </header>

      <!-- UNDER THE HEADING, OVER THE LEDGER. The map answers "where does
           this line actually go", which is the question somebody has while
           they are looking at their lines, so it sits between the two rather
           than in a corner or at the foot of a scrolling list. Full width
           because it is a destination, not a chip: the row it replaces used
           to be found, and this one is offered. -->
      <button type="button" class="map-open" @click="openMap">
        <!-- A folded map, drawn rather than fetched: three panels and the
             creases between them, in the hairline the rest of the screen is
             ruled with. -->
        <svg
          class="map-glyph"
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M1 4.2 6.2 2.2v11.6L1 15.8V4.2Z M6.2 2.2 11.8 4.2v11.6L6.2 13.8V2.2Z M11.8 4.2 17 2.2v11.6l-5.2 2V4.2Z"
            stroke="currentColor"
            stroke-width="1"
            stroke-linejoin="round"
          />
        </svg>
        <span class="map-open-name">NETWORK MAP</span>
        <span class="map-open-go" aria-hidden="true">&rarr;</span>
      </button>

      <!-- The same chips as the board and Choose lines: one setting, one per
           service that is on. They show or hide that service's lines in both
           sections below. -->
      <div class="home-chips">
        <ModeFilter compact part="chips" :chips="filter.chips.value" :on="filter.on.value" @toggle="filter.toggle" />
      </div>

      <LineLedger :lines="shownLines" :filtered-out="lines.length > 0 && !shownLines.length" :others="shownOthers" :timetable-only="notLive" :city="city" :group-names="cityInfo.lineGroups" @select="openDisruption" @add="editLines">
        <!-- Under the person's own lines and above everybody else's: read
             after the rows they came for, never in front of them. -->
        <template #after-watched>
          <ModeIntro v-if="introMode" :mode="introMode" @add="addMode" @dismiss="dismissIntro" />
        </template>
      </LineLedger>

      <!-- Said under the ledger rather than in place of it: the rows are real —
           they are your lines, and tapping one still opens its strip — it is
           only the STATUS that nobody publishes. -->
      <!-- THE LINE AND STATION DATA DID NOT LOAD (twice), so the rows above have
           no names. Said, rather than left empty; it is tried again by itself
           when the phone is back online, and this goes when it lands. -->
      <p v-if="networkFailed" class="no-alerts" role="status">
        Couldn’t load the line and station data for {{ cityInfo.name }}. Check
        your connection: it will load by itself when you’re back online.
      </p>

      <p v-if="noAlerts && notLive" class="no-alerts">
        {{ cityInfo.name }} publishes its timetable and nothing live: no
        disruption notices, no live departure times, no train positions. These
        lines will always read as running to timetable, every time shown is the
        scheduled one, and the ledger cannot tell you about a closure because
        nothing is being sent.
      </p>
      <p v-else-if="noAlerts" class="no-alerts">
        {{ cityInfo.operator }} does not publish disruption notices, so these
        lines will always read as running to timetable. Departure times and
        train positions are live; the ledger cannot tell you about a closure
        because nothing is being sent.
      </p>
      <!-- One line of a city that otherwise publishes notices: Canberra's
           light rail, beside its NSW TrainLink trains that do. -->
      <p v-else-if="cityInfo.quietNote" class="no-alerts">{{ cityInfo.quietNote }}</p>

      <div class="spacer" aria-hidden="true" />

      <!-- THE FOOTER IS NOT CONDITIONAL ANY MORE. It used to render only when
           there was something in the ledger, which was fine while everything
           in it was about the ledger — and stopped being fine the moment the
           network map moved in beside "Edit lines": somebody who had just
           switched city, and so watched nothing, had no way to reach the map
           at all. The map has since moved to the masthead, where it is offered
           whether or not anything is watched and without a scroll; the footer
           keeps the clock and "Edit lines", which does go with the ledger,
           because the empty state above already offers its own "Add lines". -->
      <footer class="footer">
        <span class="foot-left">
          <span v-if="quietHours" class="quiet">
            QUIET {{ quietHours.from }}&ndash;{{ quietHours.until }}<template
              v-if="quietHours.more"
            > +{{ quietHours.more }}</template>
          </span>
          <!-- The ledger is what it last was. This is how long ago that was. -->
          <span v-if="asOf" class="quiet">{{ asOf }}</span>
        </span>
        <span class="foot-right">
          <button v-if="lines.length" type="button" class="la-button edit" @click="editLines">
            Edit lines
          </button>
        </span>
      </footer>
    </ion-content>
  </ion-page>
</template>

<style scoped>

/* The map, offered rather than hidden: the full width of the page, the
   ledger's own frame, and the hairline it is ruled with. Mono caps because it
   is a control, and the glyph before the name because a map is a thing you
   recognise faster than you read. */
.home-chips { padding: 12px 18px; }
.map-open {
  display: flex;
  align-items: center;
  gap: 10px;
  width: calc(100% - 36px);
  min-height: 52px;
  margin: 0 18px 14px;
  padding: 0 14px;
  background: var(--la-platform);
  border: 0.5px solid var(--la-rule);
  border-radius: 3px;
  color: var(--la-enamel);
  font-family: var(--la-font-mono);
  font-size: var(--la-control-mono);
  letter-spacing: var(--la-control-track);
  cursor: pointer;
  transition: border-color var(--la-dur) ease;
}

.map-open:hover { border-color: var(--la-enamel); }
.map-open:focus-visible { outline: 2px solid var(--la-enamel); outline-offset: 2px; }

.map-glyph { flex-shrink: 0; color: var(--la-dim); }

.map-open-name { flex: 1; text-align: left; }

.map-open-go { color: var(--la-dim); }
.page {
  --background: var(--la-carriage);
  --padding-bottom: env(safe-area-inset-bottom);
}

/* Prose, at the weight of a footnote. Not a banner and not an error: nothing
   has gone wrong, there is simply a thing this network does not do. */
.no-alerts {
  margin: 12px 16px 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--la-dim);
}

/* The footer sits at the BOTTOM of the screen, not directly under the last
   row. Three watched lines leave two thirds of the page empty, and a rule with
   a button hanging in the middle of that reads as the end of a list that has
   more below it. Ionic's content is a shadow host, so the column has to be
   declared on the part rather than on .page. */
.page::part(scroll) {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.spacer {
  flex: 1;
}

.footer {
  margin-top: 2px;
  padding: 18px;
  border-top: 0.5px solid var(--la-rule);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.foot-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.quiet {
  font-family: var(--la-font-mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  color: var(--la-dim);
}

.foot-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.edit {
  padding: 0 16px;
  font-size: 13px;
}

/* The shared-link note. Dim mono under the title, at the width of a sentence,
   and dismissible because it is about how you got here and not about this
   screen. Same words and same shape as the board's — see src/share-open.js. */
.share-note {
  display: block;
  width: 100%;
  margin: 8px 0 0;
  padding: 0;
  border: 0;
  background: transparent;
  font-family: var(--la-font-mono);
  font-size: 11px;
  line-height: 1.5;
  letter-spacing: 0.04em;
  color: var(--la-dim);
  text-align: left;
  cursor: pointer;
}

.share-note:focus-visible { outline: 2px solid var(--la-enamel); outline-offset: 2px; }
</style>
